import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useCancelBudgetRequestMutation, useCreateBudgetChangeRequestMutation } from "state/rtkQuery/user/userMutations";
import { useGetMyBudgetRequestsQuery, useGetUserBudgetQuery } from "state/rtkQuery/user/userQueries";
import { usePermission } from "components/RequirePermission/useAccessPermissions";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";
import { showToast } from "utils/reusableToast";

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
// A darker gold than the brand's usual #FFCD00 — that pale yellow has poor
// contrast as text/progress-fill on a white background (hard to read at any
// size), so this tier uses a legible variant instead while keeping the same
// green -> gold -> orange -> red severity progression.
const USAGE_YELLOW = "#B8860B";

const MIN_REQUEST_USD = 10;
const MAX_REQUEST_USD = 9000;
const ABSOLUTE_CAP_USD = 10000;
const MY_REQUESTS_PER_PAGE = 5;

// Shared sizing so every field in the form reads at the same, more compact
// scale as its label — MUI's default input/helper-text font is noticeably
// larger than the 0.8rem labels used throughout, which made the form look
// oversized next to the rest of the (denser) admin/portal UI.
const inputSx = {
  "& .MuiInputBase-input": { fontSize: "0.82rem" },
  "& .MuiFormHelperText-root": { fontSize: "0.68rem", mx: 0 },
};

const MY_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const getBudgetColor = (pct) => {
  if (pct >= 80) return USAGE_RED;
  if (pct >= 50) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const formatUsd = (v) =>
  "$" + Number(v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// USD amounts as floats can accumulate rounding error (e.g. 0.1 + 0.2 !==
// 0.3), so budget comparisons are done in integer cents rather than on the
// raw float values.
const toCents = (v) => Math.round(Number(v ?? 0) * 100);

// GitHub Copilot budgets apply in priority order — a per-user ULB first,
// then a cost-center budget, then the universal (enterprise-wide) budget —
// and only one is ever effective at a time. See
// https://docs.github.com/en/copilot/concepts/billing/budgets-for-usage-based-billing
const BUDGET_SCOPE_LABELS = {
  user: "Your individual budget (ULB)",
  multi_user_cost_center: "Your cost center's budget",
  multi_user_customer: "The universal (enterprise-wide) budget",
};

const budgetScopeLabel = (scope) => BUDGET_SCOPE_LABELS[scope] ?? null;

const STATUS_STYLES = {
  pending: { label: "Pending", color: "#F28C00", bg: "rgba(242,140,0,0.1)" },
  approved: { label: "Approved", color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  rejected: { label: "Rejected", color: USAGE_RED, bg: "rgba(226,35,26,0.1)" },
  failed: { label: "Failed", color: "#C53211", bg: "rgba(197,50,17,0.12)" },
  cancelled: { label: "Cancelled", color: "#666", bg: "rgba(0,0,0,0.06)" },
};

const StatusChip = ({ status }) => {
  const { label, color, bg } = STATUS_STYLES[status] ?? STATUS_STYLES.pending;
  return <Chip label={label} size="small" sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.72rem" }} />;
};

const formatSignedUsd = (v, action) => {
  const sign = action === "decrease" ? "-" : "+";
  return `${sign} ${formatUsd(Math.abs(Number(v ?? 0)))}`;
};

// The backend sends naive UTC timestamps with no timezone designator (e.g.
// "2026-08-28T10:02:40.159000", no trailing "Z") — the JS Date constructor
// treats a designator-less ISO string as *local* time, not UTC, which would
// silently show the wrong time (or, near a UTC-day boundary, the wrong date)
// in any timezone other than UTC. Appending "Z" (if not already present)
// forces correct UTC interpretation before converting to the viewer's local
// time/date.
const asUtcDate = (isoDate) => {
  const utcString = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoDate) ? isoDate : `${isoDate}Z`;
  return new Date(utcString);
};

const formatDateTime = (isoDate) => (isoDate ? asUtcDate(isoDate).toLocaleDateString("en-US") : "—");

// Full date + time in the viewer's own local timezone (toLocaleString with no
// explicit timeZone uses the browser's) — used for "last updated" so it's
// unambiguous which moment the displayed budget/usage figures reflect.
// hour12 is forced explicitly so the time always shows an AM/PM suffix
// regardless of the browser's locale (some locales default to 24-hour time),
// and timeZoneName appends the zone abbreviation (e.g. "EDT", "GMT+5:30") so
// it's clear which timezone the timestamp was converted into.
const formatUpdatedAt = (isoDate) => {
  if (!isoDate) return null;
  const date = asUtcDate(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  const datePart = date.toLocaleDateString(undefined, { dateStyle: "medium" });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  return `${datePart}, ${timePart}`;
};

const MyRequestsList = ({
  requests,
  isLoading,
  statusFilter,
  onStatusFilterChange,
  currentPage,
  setCurrentPage,
  nPages,
  orgsById,
  onCancelRequest,
  isCancelling,
  canCancel,
}) => (
  <>
    <Box sx={{ mb: 1.5, minWidth: 160 }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#333", mb: 0.5 }}>Status</Typography>
      <Select
        size="small"
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value)}
        sx={{ backgroundColor: "#fff", fontSize: "0.82rem", minWidth: 160 }}
      >
        {MY_STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>

    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress size={22} sx={{ color: ACCENT }} />
      </Box>
    ) : !requests.length ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <Typography sx={{ fontSize: "0.85rem", color: "#bbb", fontStyle: "italic" }}>
          You haven't submitted any budget requests yet.
        </Typography>
      </Box>
    ) : (
      <>
        <Box sx={{ maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.2, pr: 0.5 }}>
          {requests.map((r) => {
            const isIncrease = r.action === "increase";
            const color = isIncrease ? ACCENT : USAGE_RED;
            const statusStyle = STATUS_STYLES[r.status] ?? STATUS_STYLES.pending;
            return (
              <Box
                key={r.request_id}
                sx={{
                  border: "1px solid #e8e8e8",
                  borderLeft: `3px solid ${statusStyle.color}`,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  p: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color }}>
                      {isIncrease ? <NorthEastIcon sx={{ fontSize: "1rem" }} /> : <SouthWestIcon sx={{ fontSize: "1rem" }} />}
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color }}>
                        {formatSignedUsd(r.change_amount, r.action)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                      {orgsById?.[r.org_id]?.name ?? r.org_id} · {formatDateTime(r.request_date)}
                    </Typography>
                  </Box>
                  {r.request_reason && (
                    <Typography sx={{ fontSize: "0.76rem", color: "#555", mt: 0.4 }}>{r.request_reason}</Typography>
                  )}
                  {(r.status === "approved" || r.status === "rejected") && (
                    <Typography
                      sx={{ fontSize: "0.72rem", color: r.status === "rejected" ? USAGE_RED : ACCENT, mt: 0.5 }}
                    >
                      {r.status === "rejected" ? "Rejected" : "Approved"} by {r.reviewed_by ?? "—"} on{" "}
                      {formatDateTime(r.reviewed_at)}
                      {r.action_reason ? ` — ${r.action_reason}` : ""}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.6 }}>
                  <StatusChip status={r.status} />
                  {r.status === "pending" && (
                    <Tooltip title={canCancel ? "" : "You don't have permission to cancel budget requests"}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onCancelRequest(r)}
                          disabled={isCancelling || !canCancel}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            color: USAGE_RED,
                            borderColor: "rgba(226,35,26,0.4)",
                            borderRadius: "6px",
                            px: 1,
                            py: 0.2,
                            minWidth: 0,
                            lineHeight: 1.6,
                            "&:hover": { borderColor: USAGE_RED, backgroundColor: "rgba(226,35,26,0.06)" },
                          }}
                        >
                          {isCancelling ? <CircularProgress size={12} sx={{ color: USAGE_RED }} /> : "Cancel"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        {nPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
            <Pagination nPages={nPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </Box>
        )}
      </>
    )}
  </>
);

const UlbRequestModal = ({ open, onClose, email, platformId }) => {
  const [tab, setTab] = useState(0);
  const [orgId, setOrgId] = useState("");
  const [action, setAction] = useState("increase");
  const [amount, setAmount] = useState("");
  const [justification, setJustification] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleteWarningAcked, setDeleteWarningAcked] = useState(false);

  // The user's AI Credit budget summary: every scope that could apply to them
  // (enterprise/cost-center/user), which one is currently effective, any open
  // increase/decrease request, and which orgs they hold a Copilot license in.
  // Skipped while closed (not just when email/platformId are missing) — the
  // Dialog stays mounted in NavBar the whole time (only `open` toggles), so
  // without this the query subscribes once and never resubscribes on reopen,
  // meaning the endpoint's forceRefetch never gets a chance to fire again and
  // a stale budget (e.g. before an admin's approval) can keep showing.
  const { data: budgetData, isFetching: loadingBudget } = useGetUserBudgetQuery(
    { email, platformId },
    { skip: !open || !email || !platformId }
  );

  // Every active org in the platform — filtered down below to just the ones
  // the user is actually licensed for, to populate the org dropdown.
  const { data: allOrgs, isFetching: loadingAllOrgs } = useGetActiveOrganizationsQuery(
    { platformId },
    { skip: !platformId }
  );
  const loadingOrgs = loadingBudget || loadingAllOrgs;

  const licensedOrgIds = budgetData?.licensed_organizations ?? [];
  const myOrgs = useMemo(
    () => (allOrgs ?? []).filter((org) => licensedOrgIds.includes(org._id)),
    [allOrgs, licensedOrgIds]
  );
  // Unfiltered by license, so past requests against an org the user is no
  // longer licensed for still resolve to a name in the "My Requests" tab.
  const orgsById = useMemo(
    () => Object.fromEntries((allOrgs ?? []).map((org) => [org._id, org])),
    [allOrgs]
  );

  // The user's own budget request history, across every org and status.
  // Skipped while closed for the same reason as getUserBudget above — an
  // admin may have reviewed a request since this was last open, and a
  // reopen should show its current status, not a subscription-stale one.
  const [myRequestsStatusFilter, setMyRequestsStatusFilter] = useState("all");
  const [myRequestsPage, setMyRequestsPage] = useState(1);
  const { data: myRequestsData, isFetching: loadingMyRequests } = useGetMyBudgetRequestsQuery(
    { email, platformId, status: myRequestsStatusFilter, page: myRequestsPage, perPage: MY_REQUESTS_PER_PAGE },
    { skip: !open || !email || !platformId }
  );
  const myRequests = myRequestsData?.requests ?? [];
  const myRequestsTotal = myRequestsData?.total ?? 0;
  const myRequestsPages = Math.max(1, Math.ceil(myRequestsTotal / MY_REQUESTS_PER_PAGE));

  const handleMyRequestsStatusFilterChange = (value) => {
    setMyRequestsStatusFilter(value);
    setMyRequestsPage(1);
  };

  const [cancelBudgetRequest, { isLoading: cancellingRequest }] = useCancelBudgetRequestMutation();
  const canCancelRequest = usePermission(PERMISSIONS.COPILOT_BUDGET_REQUEST_ID_DELETE);

  // The row's Cancel link just opens this confirmation; the mutation only
  // fires once the user confirms in the dialog below.
  const [requestPendingCancel, setRequestPendingCancel] = useState(null);

  const confirmCancelRequest = async () => {
    if (!requestPendingCancel) return;
    try {
      await cancelBudgetRequest({ email, platformId }).unwrap();
      showToast({
        title: "Request cancelled",
        description: "Your budget change request has been cancelled.",
        type: "success",
        id: "ulb-cancel-toast",
      });
      setRequestPendingCancel(null);
    } catch (error) {
      showToast({
        title: "Cancel failed",
        description: "We couldn't cancel this request. Please try again.",
        type: "error",
        id: "ulb-cancel-error-toast",
      });
    }
  };

  // `effective_budget` only carries id/amount/consumed — match it back against
  // `budgets[]` (which has the full scope/entity info) to tell the user
  // whether what's currently applied to them is their own individual budget
  // or a shared enterprise/cost-center one.
  const effectiveBudgetDetail = (budgetData?.budgets ?? []).find(
    (b) => b.id === budgetData?.effective_budget?.id
  );

  const currentBudget = budgetData?.effective_budget
    ? {
        budget_amount: budgetData.effective_budget.budget_amount,
        consumed_amount: budgetData.effective_budget.consumed_amount,
        budget_scope: effectiveBudgetDetail?.budget_scope,
      }
    : null;

  // Cost center is a hard floor for a ULB — a request whose resulting total
  // lands at or below it is redundant, since the user would inherit the
  // cost-center amount regardless. Universal isn't a floor, but an exact
  // match to it is equally redundant. Either way the backend deletes the
  // ULB instead of patching it, and the user's effective budget reverts to
  // whichever shared budget it inherits — not the number they requested —
  // so this is surfaced before submit, same rule the admin-side bulk budget
  // change modal uses. Scoped to ai_credits since a user can also hold a
  // premium_requests budget at the same scope.
  const aiCreditsBudgets = (budgetData?.budgets ?? []).filter((b) => b.budget_product_sku === "ai_credits");
  const hasUlb = aiCreditsBudgets.some((b) => b.budget_scope === "user");
  const costCenterBudget = aiCreditsBudgets.find((b) => b.budget_scope === "multi_user_cost_center");
  const universalBudget = aiCreditsBudgets.find((b) => b.budget_scope === "multi_user_customer");

  // An open request is any non-empty `open_request` object on the budget
  // response — while one is in flight, the user can't submit another.
  const openRequest =
    budgetData?.open_request && Object.keys(budgetData.open_request).length > 0
      ? budgetData.open_request
      : null;

  const [createBudgetChangeRequest, { isLoading: submitting }] = useCreateBudgetChangeRequestMutation();

  const pct =
    currentBudget && currentBudget.budget_amount > 0
      ? Math.min((currentBudget.consumed_amount / currentBudget.budget_amount) * 100, 100)
      : 0;

  const parsedAmount = Number(amount);
  const amountIsNumber = amount !== "" && !Number.isNaN(parsedAmount);

  const newTotal =
    action === "increase"
      ? (currentBudget?.budget_amount ?? 0) + (amountIsNumber ? parsedAmount : 0)
      : (currentBudget?.budget_amount ?? 0) - (amountIsNumber ? parsedAmount : 0);

  // Cost center is a hard floor on a decrease request — the resulting total
  // can never go below it, since that's the minimum the user would inherit
  // regardless of what they type. Increase requests only warn on landing
  // exactly on a floor/redundant amount (never blocked, since increasing
  // away from a floor is always fine); the hard block below the floor is
  // decrease-only, matching the direction the floor can actually be crossed.
  const belowCostCenterFloor =
    action === "decrease" &&
    hasUlb &&
    costCenterBudget &&
    amountIsNumber &&
    toCents(newTotal) < toCents(costCenterBudget.budget_amount);

  // Warn only on an exact match to the floor/universal amount — a decrease
  // landing strictly below the cost-center floor is excluded entirely above
  // (belowCostCenterFloor blocks submission for that case), and an increase
  // that still lands below the floor isn't flagged: it's just a smaller
  // increase than the floor, not an obviously redundant one.
  let willDeleteUlb = false;
  let resultingBudgetAmount = null;
  let resultingBudgetLabel = null;
  if (hasUlb && amountIsNumber && !belowCostCenterFloor) {
    if (costCenterBudget) {
      if (toCents(newTotal) === toCents(costCenterBudget.budget_amount)) {
        willDeleteUlb = true;
        resultingBudgetAmount = costCenterBudget.budget_amount;
        resultingBudgetLabel = "cost center budget";
      }
    } else if (universalBudget && toCents(newTotal) === toCents(universalBudget.budget_amount)) {
      willDeleteUlb = true;
      resultingBudgetAmount = universalBudget.budget_amount;
      resultingBudgetLabel = "universal budget";
    }
  }

  const amountError = useMemo(() => {
    if (!amountIsNumber) return null;
    if (action === "increase") {
      if (parsedAmount < MIN_REQUEST_USD) return `Minimum request is ${formatUsd(MIN_REQUEST_USD)}`;
      if (parsedAmount > MAX_REQUEST_USD) return `Maximum request is ${formatUsd(MAX_REQUEST_USD)}`;
      if (newTotal > ABSOLUTE_CAP_USD) return `New total would exceed the ${formatUsd(ABSOLUTE_CAP_USD)} cap`;
    } else {
      if (parsedAmount <= 0) return "Enter an amount greater than $0";
      if (parsedAmount > (currentBudget?.budget_amount ?? 0)) return "Cannot decrease by more than your current budget";
      if (belowCostCenterFloor) {
        return `The budget cannot be reduced below your cost center budget of ${formatUsd(costCenterBudget.budget_amount)}.`;
      }
    }
    return null;
  }, [action, amountIsNumber, parsedAmount, newTotal, currentBudget, belowCostCenterFloor, costCenterBudget]);

  const justificationError =
    justification.length > 0 && justification.trim().length < 20
      ? "Business justification must be at least 20 characters"
      : null;

  const canSubmit =
    orgId &&
    amountIsNumber &&
    !amountError &&
    justification.trim().length >= 20 &&
    acknowledged &&
    (!willDeleteUlb || deleteWarningAcked) &&
    !submitting &&
    !openRequest;

  const resetAndClose = () => {
    setTab(0);
    setOrgId("");
    setAction("increase");
    setAmount("");
    setJustification("");
    setAcknowledged(false);
    setDeleteWarningAcked(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await createBudgetChangeRequest({
        email,
        platformId,
        orgId,
        action,
        amount: parsedAmount,
        reason: justification.trim(),
      }).unwrap();

      showToast({
        title: "Request submitted",
        description: "Your budget change request is now pending approval.",
        type: "success",
        id: "ulb-request-toast",
      });
      resetAndClose();
    } catch (error) {
      showToast({
        title: "Request failed",
        // A 400 can mean an earlier request is still outstanding (pending, or failed and awaiting
        // an admin retry) — show the API's own reason when it gives one.
        description:
          error?.data?.message || error?.data?.error_message || "We couldn't submit your budget change request. Please try again.",
        type: "error",
        id: "ulb-request-error-toast",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `3px solid ${ACCENT}`,
          py: 2,
          px: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
            Manage My AI Credit Budget
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#999", mt: 0.3 }}>
            {tab === 0
              ? "Submit a request to increase or decrease your Copilot AI Credit budget"
              : "Your past budget change requests, across every org"}
          </Typography>
        </Box>
        <IconButton onClick={resetAndClose} size="small" sx={{ color: "#555" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: "1px solid #f0f0f0", px: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 40 },
            "& .Mui-selected": { color: `${ACCENT} !important` },
            "& .MuiTabs-indicator": { backgroundColor: ACCENT },
          }}
        >
          <Tab label="New Request" />
          <Tab label="My Requests" />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {tab === 1 ? (
          <MyRequestsList
            requests={myRequests}
            isLoading={loadingMyRequests}
            statusFilter={myRequestsStatusFilter}
            onStatusFilterChange={handleMyRequestsStatusFilterChange}
            currentPage={myRequestsPage}
            setCurrentPage={setMyRequestsPage}
            nPages={myRequestsPages}
            orgsById={orgsById}
            onCancelRequest={setRequestPendingCancel}
            isCancelling={cancellingRequest}
            canCancel={canCancelRequest}
          />
        ) : (
          <>
            {/* Current budget preview, compacted to a single strip — enterprise-wide,
            same number regardless of which org is picked below, since Copilot AI
            credits are pooled per enterprise (one license per user), not scoped
            per organization. */}
            <Box sx={{ mb: 2, p: 1.2, border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fafafa" }}>
              {loadingBudget ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: ACCENT }} />
                  <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>Loading your budget…</Typography>
                </Box>
              ) : currentBudget ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "#1a1a1a" }}>
                      Current budget: <strong>{formatUsd(currentBudget.consumed_amount)}</strong> used of{" "}
                      <strong>{formatUsd(currentBudget.budget_amount)}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: getBudgetColor(pct), whiteSpace: "nowrap" }}>
                      {pct.toFixed(0)}%
                    </Typography>
                  </Box>
                  {budgetScopeLabel(currentBudget.budget_scope) && (
                    <Typography sx={{ fontSize: "0.68rem", color: "#999", mt: 0.2 }}>
                      {budgetScopeLabel(currentBudget.budget_scope)} is currently applied to you.
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 5,
                      borderRadius: 4,
                      mt: 0.6,
                      backgroundColor: "#eee",
                      "& .MuiLinearProgress-bar": { backgroundColor: getBudgetColor(pct), borderRadius: 4 },
                    }}
                  />
                  {formatUpdatedAt(budgetData?.updated_at) && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#888", mt: 0.6 }}>
                      Last updated {formatUpdatedAt(budgetData.updated_at)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>
                  We couldn't find a budget for your account.
                </Typography>
              )}
            </Box>

            {openRequest && (
              <Box sx={{ mb: 2, p: 1.2, border: `1px solid ${USAGE_ORANGE}`, borderRadius: "10px", backgroundColor: "rgba(242,140,0,0.08)" }}>
                <Typography sx={{ fontSize: "0.78rem", color: "#7a4a00" }}>
                  You already have an open request to <strong>{openRequest.action}</strong> your budget by{" "}
                  <strong>{formatUsd(openRequest.change_amount)}</strong>, submitted{" "}
                  {new Date(openRequest.request_date).toLocaleDateString("en-US")}. You can't submit another until it's resolved.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
              {/* Org — informational only, doesn't scope the budget number itself */}
              <Box sx={{ flex: 1.2, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 28, mb: 0.7 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333" }}>
                    Organization <span style={{ color: USAGE_RED }}>*</span>
                  </Typography>
                </Box>
                <Select
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  disabled={loadingOrgs}
                  renderValue={(value) => {
                    if (!value) return <span style={{ color: "#999" }}>-- Select an organization --</span>;
                    return myOrgs.find((o) => o._id === value)?.name ?? "Loading…";
                  }}
                  sx={{ backgroundColor: "#fff", fontSize: "0.82rem" }}
                >
                  {myOrgs.map((o) => (
                    <MenuItem key={o._id} value={o._id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Amount — the increase/decrease toggle sits on the same label
                  row (word-labeled, not icon-only) instead of its own full row. */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 28, mb: 0.7 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333" }}>
                    Amount (USD) <span style={{ color: USAGE_RED }}>*</span>
                  </Typography>
                  <ToggleButtonGroup
                    value={action}
                    exclusive
                    size="small"
                    onChange={(e, v) => {
                      if (!v) return;
                      setAction(v);
                      setDeleteWarningAcked(false);
                    }}
                    sx={{
                      "& .MuiToggleButton-root": {
                        px: 1,
                        py: 0.15,
                        gap: 0.4,
                        fontSize: "0.7rem",
                        textTransform: "none",
                        color: "#999",
                        borderColor: "#e0e0e0",
                      },
                    }}
                  >
                    {/* Selected side gets a soft tint + border (not just colored text) so the
                        active choice is clear without relying on red/green alone. */}
                    <ToggleButton
                      value="increase"
                      sx={{
                        "&:hover": { backgroundColor: "rgba(38,137,13,0.06)" },
                        "&.Mui-selected": {
                          color: ACCENT,
                          fontWeight: 700,
                          backgroundColor: "rgba(38,137,13,0.10)",
                          borderColor: ACCENT,
                          zIndex: 1,
                          "&:hover": { backgroundColor: "rgba(38,137,13,0.16)" },
                        },
                      }}
                    >
                      <NorthEastIcon sx={{ fontSize: "0.85rem" }} />
                      Increase
                    </ToggleButton>
                    <ToggleButton
                      value="decrease"
                      sx={{
                        "&:hover": { backgroundColor: "rgba(226,35,26,0.05)" },
                        "&.Mui-selected": {
                          color: USAGE_RED,
                          fontWeight: 700,
                          backgroundColor: "rgba(226,35,26,0.08)",
                          borderColor: USAGE_RED,
                          zIndex: 1,
                          "&:hover": { backgroundColor: "rgba(226,35,26,0.14)" },
                        },
                      }}
                    >
                      <SouthWestIcon sx={{ fontSize: "0.85rem" }} />
                      Decrease
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="e.g. 25"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setDeleteWarningAcked(false);
                  }}
                  error={Boolean(amountError)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={inputSx}
                />
              </Box>
            </Box>
            <Typography sx={{ fontSize: "0.8rem", color: "#999", mb: 2 }}>
              {amountError ??
                (amountIsNumber ? (
                  <>
                    New monthly budget would be{" "}
                    <Box component="span" sx={{ fontWeight: 800, color: "#1a1a1a" }}>
                      {formatUsd(newTotal)}
                    </Box>
                  </>
                ) : action === "increase" ? (
                  `Between ${formatUsd(MIN_REQUEST_USD)} and ${formatUsd(MAX_REQUEST_USD)} per request — org is just for routing/context, your budget is enterprise-wide`
                ) : (
                  "Cannot exceed your current budget"
                ))}
            </Typography>

            {willDeleteUlb && (
              <Box
                sx={{
                  backgroundColor: "#fdecea",
                  border: `1px solid ${USAGE_RED}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: "0.76rem", color: "#8e0000", fontWeight: 600, mb: 0.7 }}>
                  Your requested budget matches your {resultingBudgetLabel === "cost center budget" ? "cost center" : "universal"} budget of {formatUsd(resultingBudgetAmount)}. If approved, your individual user-level budget of {formatUsd(currentBudget?.budget_amount ?? 0)} will be removed, and you will inherit the {formatUsd(resultingBudgetAmount)} {resultingBudgetLabel === "cost center budget" ? "cost center" : "universal"} budget.
                </Typography>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={deleteWarningAcked}
                      onChange={(e) => setDeleteWarningAcked(e.target.checked)}
                      sx={{ p: 0.5, color: USAGE_RED, "&.Mui-checked": { color: USAGE_RED } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.74rem", color: "#333" }}>
                      I understand this may delete my individual budget override
                    </Typography>
                  }
                />
              </Box>
            )}

            {/* Justification */}
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
              Business justification <span style={{ color: USAGE_RED }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Working on project X which requires heavy use of Claude Opus for…"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              error={Boolean(justificationError)}
              helperText={justificationError ?? "Minimum 20 characters"}
              sx={{ mb: 1.5, ...inputSx }}
            />

            <FormControlLabel
              control={
                <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} size="small" />
              }
              label={
                <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>
                  I understand AI Credit requests are billable and my manager is aware.
                </Typography>
              }
            />
          </>
        )}
      </DialogContent>

      {tab === 0 && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={resetAndClose} sx={{ textTransform: "none", color: "#555" }}>
            Cancel
          </Button>
          <Tooltip
            title={
              openRequest
                ? "An open request is already in progress"
                : willDeleteUlb && !deleteWarningAcked
                  ? "Confirm you understand this may delete your individual budget override before continuing."
                  : ""
            }
          >
            <span>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                variant="contained"
                sx={{
                  textTransform: "none",
                  backgroundColor: ACCENT,
                  "&:hover": { backgroundColor: "#1e6b0a" },
                }}
              >
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit Request"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      )}

      {/* Cancel-request confirmation, styled to match the rest of this
          modal's dialogs (accent-bordered title, Box/Typography layout). */}
      <Dialog
        open={Boolean(requestPendingCancel)}
        onClose={cancellingRequest ? undefined : () => setRequestPendingCancel(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${USAGE_RED}`,
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
            Cancel Budget Request
          </Typography>
          <IconButton
            size="small"
            onClick={() => setRequestPendingCancel(null)}
            disabled={cancellingRequest}
            sx={{ color: "#555" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2.5, "&.MuiDialogContent-root": { pt: 1.5 } }}>
          {requestPendingCancel && (
            <>
              <Typography sx={{ fontSize: "0.85rem", color: "#1a1a1a", mb: 1.5 }}>
                Are you sure you want to cancel your budget change request for{" "}
                <strong>
                  "{orgsById?.[requestPendingCancel.org_id]?.name ?? requestPendingCancel.org_id}"
                </strong>
                ?
              </Typography>
              <Box sx={{ border: "1px solid #e8e8e8", borderRadius: "8px", p: 1.2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>Existing budget</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1a1a1a" }}>
                    {formatUsd(currentBudget?.budget_amount)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>Action</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1a1a1a", textTransform: "capitalize" }}>
                    {requestPendingCancel.action}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>Change amount</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1a1a1a" }}>
                    {formatSignedUsd(requestPendingCancel.change_amount, requestPendingCancel.action)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button
            onClick={() => setRequestPendingCancel(null)}
            disabled={cancellingRequest}
            sx={{ textTransform: "none", color: "#555" }}
          >
            Keep Request
          </Button>
          <Button
            onClick={confirmCancelRequest}
            variant="contained"
            disabled={cancellingRequest}
            sx={{
              textTransform: "none",
              backgroundColor: USAGE_RED,
              "&:hover": { backgroundColor: "#b81d15" },
            }}
          >
            {cancellingRequest ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cancel Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UlbRequestModal;
